import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { Tooltip } from '../Tooltip';

/**
 * FAIXA DE ATIVIDADE — um quadradinho por dia, a história do mês numa linha.
 *
 * Referência trazida pelo dono (19/09): a tira verde/vermelha na tela do monitorador.
 *
 * ### O que ela responde, e que nenhum número responde
 *
 * "Este número caiu quantas vezes no mês?" O painel sabe dizer se o monitorador está
 * conectado AGORA; o que ele não dizia é se ficou de pé o mês inteiro ou se cai toda
 * terça-feira. Para quem põe verba em cima disso, a diferença entre "caiu uma vez" e "cai
 * sempre" muda a decisão — e ela só aparece quando os dias ficam lado a lado.
 *
 * ### Três estados, e o terceiro é o que costuma faltar
 *
 * - **ok** — houve medição e estava de pé;
 * - **falha** — houve medição e estava fora;
 * - **sem medição** — ninguém olhou naquele dia (o sistema estava desligado, ou o
 *   monitorador nem existia ainda). Não é falha.
 *
 * Pintar "sem medição" de vermelho seria transformar o nosso silêncio no fracasso do cliente.
 * Pintar de verde seria pior: um mês inteiro de verde que não aconteceu. Ele fica com a
 * HACHURA (`bg-hatch`), o único padrão de fundo do produto, que sempre quer dizer "não
 * sabemos" — e a legenda diz o que é.
 */
export type DiaAtividade = {
  /** `YYYY-MM-DD` no fuso do workspace. */
  dia: string;
  estado: 'ok' | 'falha' | 'sem-medicao';
  /** Texto do tooltip. Sem isto o quadradinho é enfeite. */
  detalhe?: string;
};

export interface ActivityHeatmapProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  dias: readonly DiaAtividade[];
  /** O que a faixa cobre, para leitor de tela (ex.: "Conectividade do monitorador"). */
  label: string;
  size?: 'sm' | 'md';
  /** Mostra a legenda embaixo. Ligada por padrão: cor sem legenda é adivinhação. */
  legenda?: boolean;
}

const COR: Record<DiaAtividade['estado'], string> = {
  ok: 'bg-success',
  falha: 'bg-danger',
  'sem-medicao': 'bg-muted bg-hatch',
};

const NOME: Record<DiaAtividade['estado'], string> = {
  ok: 'no ar',
  falha: 'fora do ar',
  'sem-medicao': 'sem medição',
};

const diaLegivel = (dia: string): string => {
  const [ano, mes, d] = dia.split('-');
  return d && mes && ano ? `${d}/${mes}/${ano}` : dia;
};

export function ActivityHeatmap({
  dias,
  label,
  size = 'md',
  legenda = true,
  className,
  ...props
}: ActivityHeatmapProps) {
  const falhas = dias.filter((d) => d.estado === 'falha').length;
  const medidos = dias.filter((d) => d.estado !== 'sem-medicao').length;

  return (
    <div className={cn('flex flex-col gap-1.5', className)} {...props}>
      <div
        className="flex flex-wrap items-center gap-0.75"
        role="img"
        aria-label={
          medidos === 0
            ? `${label}: nenhum dia medido ainda`
            : `${label}: ${falhas} de ${medidos} dias medidos ficaram fora do ar`
        }
      >
        {dias.map((d) => (
          <Tooltip
            key={d.dia}
            content={`${diaLegivel(d.dia)} — ${NOME[d.estado]}${d.detalhe ? `. ${d.detalhe}` : ''}`}
          >
            <span
              tabIndex={0}
              aria-hidden="true"
              className={cn(
                'rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                size === 'sm' ? 'size-2.5' : 'size-3.5',
                COR[d.estado],
              )}
            />
          </Tooltip>
        ))}
      </div>

      {legenda ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs font-normal text-muted-foreground">
          {(['ok', 'falha', 'sem-medicao'] as const).map((estado) => (
            <span key={estado} className="inline-flex items-center gap-1">
              <span
                aria-hidden="true"
                className={cn('size-2.5 rounded-xs', COR[estado])}
              />
              {NOME[estado]}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
